import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { Box, Chip, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import FileUploadService from '../../../../shared/services/file-upload/file-upload.service';
import ConversionSourceYearService from '../../../../shared/services/conversion-source-year/conversion-source-year.service';
import { IConversionSourceWithConversions, IConversionYear } from '../../../../shared/services/conversion-source/conversion-source.model';
import { ConversionFactorsUploadDialog } from '../../../dialogs/super-admin/conversion-factors-upload-dialog';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { endYear, startYear } from '../../../../shared/utils/conversion-utils';
import ConversionFactorCustomService from '../../../../shared/services/conversion-factor-custom/conversion-factor-custom-service';
import { ConversionFactorCustomAddNewYearDialog } from '../../../dialogs/super-admin/conversion-factor-custom-add-new-year-dialog';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { IConversionFactorCustom } from '../../../../shared/services/conversion-factor-custom/conversion-factor-custom.model';
import { secrSourceLabelForKey } from '../../../../shared/constants/secr-reporting-sources';

export const SuperAdminConversionFactorsYearsPage = () => {
  const { conversionSourceId } = useParams();
  const navigate = useNavigate();
  //Services
  const userNotificationsContext = useContext(UserNotificationsContext);
  const conversionSourceService = new ConversionSourceService();
  const conversionSourceYearService = new ConversionSourceYearService();
  const conversionFactorCustomService = new ConversionFactorCustomService();
  const fileUploadService = new FileUploadService();
  //State
  const [conversionSource, setConversionSource] = useState<IConversionSourceWithConversions>();
  const [conversionFactorCustoms, setConversionFactorCustoms] = useState<IConversionFactorCustom[]>([]);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const [isConversionYearDialogOpen, setIsConversionYearDialogOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>();
  const [order, setOrder] = useState<'asc' | 'desc'>();
  const [secrSearchInput, setSecrSearchInput] = useState('');
  const [secrSearch, setSecrSearch] = useState('');
  const [secrMappingFilter, setSecrMappingFilter] = useState('');
  const [secrScopeFilter, setSecrScopeFilter] = useState('');
  //Layout
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  //Variables
  const gaiaConversionSourceId = '6aa98e49-bab0-4863-bbc1-25f91d15a5db'

  const getConversionSource = () => {
    if (conversionSourceId) {
      conversionSourceService.getOne(conversionSourceId, `?includeYears=true&includePendingYears=true`)
        .then(response => {
          setConversionSource(response);
          const existingYears = new Set(response.conversionYears.map((conversionYear: IConversionYear) => String(conversionYear.year).trim()));
          const missingYears: string[] = [];
          for (let yearNumber = startYear; yearNumber <= endYear; yearNumber++) {
            const yearString = yearNumber.toString();
            if (!existingYears.has(yearString)) missingYears.push(yearString);
          }
          setAvailableYears(missingYears.sort((firstYear, secondYear) => parseInt(firstYear) - parseInt(secondYear)));
        })
        .catch((error) => {
          console.log(error)
        });
    }
  }

  const getConversionFactorCustoms = useCallback(() => {
    if (!conversionSourceId) return;
    conversionFactorCustomService
      .getAllWithPagination(inputPage, perPage, orderBy, order, {
        search: secrSearch || undefined,
        secrMappingStatus: secrMappingFilter || undefined,
        scope: secrScopeFilter || undefined,
      })
      .then((response) => {
        setConversionFactorCustoms(response.rows);
        setTotalRecords(response.count);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [
    conversionSourceId,
    inputPage,
    perPage,
    orderBy,
    order,
    secrSearch,
    secrMappingFilter,
    secrScopeFilter,
  ]);

  useEffect(() => {
    const id = window.setTimeout(() => setSecrSearch(secrSearchInput), 400);
    return () => window.clearTimeout(id);
  }, [secrSearchInput]);

  useEffect(() => {
    getConversionSource();
  }, [conversionSourceId]);

  useEffect(() => {
    if (conversionSourceId === gaiaConversionSourceId && activeTab === 1) {
      getConversionFactorCustoms();
    }
  }, [conversionSourceId, activeTab, getConversionFactorCustoms]);

  useEffect(() => {
    if (!hasPageMounted) {
      setHasPageMounted(true);
      return;
    }
    setInputPage(0);
  }, [order, orderBy, activeTab]);

  useEffect(() => {
    setInputPage(0);
  }, [secrSearch, secrMappingFilter, secrScopeFilter]);

  const secrStatusLabel = (f: IConversionFactorCustom) => {
    const s = f.secrSourceMappingStatus;
    if (s === 'confirmed') return 'Confirmed';
    if (s === 'suggested') return 'Suggested';
    if (s === 'unmapped') return 'Unmapped';
    return 'Legacy';
  };


  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, isSubHeading: false, align: 'left' as const },
    { id: 'year', numeric: false, label: 'Conversion Year', sortable: false, isHeading: true },
    ...(conversionSourceId !== gaiaConversionSourceId ? [{ id: 'status', numeric: false, label: 'Import Status', sortable: false, isSubHeading: true, align: 'left' as const }] : []),
    { id: 'active', numeric: false, label: 'Status', sortable: false, isHeading: false, align: 'left' as const },
  ];

  const sortedYearsForTable = [...(conversionSource?.conversionYears ?? [])].sort((previousYear: IConversionYear, nextYear: IConversionYear) => parseInt(nextYear.year) - parseInt(previousYear.year));
  const rows = sortedYearsForTable.map((yearObj: IConversionYear) => ({
    action: (
      <TableActionButtons
        id={yearObj.year}
        includeOther={yearObj.active ? false : true && conversionSourceId !== gaiaConversionSourceId && yearObj.mapped && !yearObj.pending}
        includeDeactivate={yearObj.active ? true : false && conversionSourceId !== gaiaConversionSourceId}
        includeView={true}
        includeDelete={!yearObj.active}
        onView={() => handleSelected([yearObj.year], yearObj.mapped, yearObj.fileUploadId ?? '')}
        onDeactivate={() => handleToggleActive(yearObj)}
        otherButtonIcon={IconName.HighlightOff}
        otherButtonText='Activate'
        onOther={() => handleToggleActive(yearObj)}
        onDelete={() => handleDelete(yearObj.fileUploadId ?? '')}
      />
    ),
    id: yearObj.year,
    year: yearObj.year,
    status: conversionSourceId !== gaiaConversionSourceId ?
      <Chip color={!yearObj.mapped ? 'info' : yearObj.importFailed ? 'error' : yearObj.pending ? 'warning' : 'success'} label={!yearObj.mapped ? 'Mapping Required' : yearObj.importFailed ? 'Import Failed' : yearObj.pending ? 'Action Required' : 'Completed'} variant='outlined' sx={{ width: '130px' }} /> : null,
    active: yearObj.active ? <Chip color='success' label='Active' variant='outlined' sx={{ width: '130px' }} /> : <Chip color='error' label='Inactive' variant='outlined' sx={{ width: '130px' }} />,
  }));

  const headCellsCustom: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, isSubHeading: false, align: 'left' as const },
    { id: 'scope', numeric: false, label: 'Scope', sortable: false, isHeading: true },
    { id: 'category', numeric: false, label: 'Category', sortable: false, isHeading: true },
    { id: 'secrSourceMappingStatus', numeric: false, label: 'SECR status', sortable: true, isHeading: false },
    { id: 'secrSuggested', numeric: false, label: 'SECR suggested', sortable: false, isHeading: false },
    { id: 'secrSourceKey', numeric: false, label: 'SECR reporting source', sortable: true, isHeading: false },
    { id: 'detailLevelOne', numeric: false, label: 'Level One', sortable: true },
    { id: 'detailLevelTwo', numeric: false, label: 'Level Two', sortable: true },
    { id: 'detailLevelThree', numeric: false, label: 'Level Three', sortable: true },
    { id: 'measurementUnit', numeric: false, label: 'Unit of Measurement', sortable: true },
    { id: 'greenHouseGasUnit', numeric: false, label: 'Greenhouse Gas Unit', sortable: true },
    { id: 'multiplier', numeric: true, label: 'Multiplier', sortable: true },
    { id: 'isRenewable', numeric: false, label: 'Renewable', sortable: true },
  ];

  const rowsCustom = conversionFactorCustoms.map(conversionFactorCustom => ({
    id: conversionFactorCustom.id,
    scope: conversionFactorCustom.scope,
    category: conversionFactorCustom.category?.name ?? '',
    secrSourceMappingStatus: (
      <Chip
        size='small'
        variant='outlined'
        label={secrStatusLabel(conversionFactorCustom)}
        color={
          conversionFactorCustom.secrSourceMappingStatus === 'confirmed'
            ? 'success'
            : conversionFactorCustom.secrSourceMappingStatus === 'unmapped'
              ? 'warning'
              : conversionFactorCustom.secrSourceMappingStatus === 'suggested'
                ? 'info'
                : undefined
        }
      />
    ),
    secrSuggested: secrSourceLabelForKey(conversionFactorCustom.secrSourceSuggestedKey),
    secrSourceKey: secrSourceLabelForKey(conversionFactorCustom.secrSourceKey),
    detailLevelOne: conversionFactorCustom.detailLevelOne,
    detailLevelTwo: conversionFactorCustom.detailLevelTwo,
    detailLevelThree: conversionFactorCustom.detailLevelThree,
    measurementUnit: conversionFactorCustom.measurementUnit?.name ?? '',
    greenHouseGasUnit: conversionFactorCustom.greenHouseGasUnit?.name ?? '',
    multiplier: conversionFactorCustom.multiplier,
    isRenewable: conversionFactorCustom.isRenewable === true ? 'Yes' : 'No',
    action: (
      <TableActionButtons
        id={conversionFactorCustom.id}
        includeEdit={true}
        onEdit={() => handleSelected([conversionFactorCustom.id], true, '')}
      />
    ),
  }));

  const handleToggleActive = async (yearObj: IConversionYear) => {
    // Deactivation withdraws the year from factor resolution for NEW calculations;
    // it is for retiring an incorrect set, not for archiving old years. Confirm
    // with the consequences and the current usage count before proceeding.
    if (yearObj.active) {
      let usageMessage = '';
      try {
        const usage = await conversionSourceYearService.getYearUsage(conversionSourceId!, yearObj.year);
        if (usage.anchoredCalculationCount > 0) {
          usageMessage = ` ${usage.anchoredCalculationCount} existing calculation(s) currently use this year's factors; they keep their stored factor until recalculated.`;
        }
      } catch (error) {
        console.error('Failed to load year usage:', error);
      }
      const confirmed = await userNotificationsContext?.openDialog({
        active: true,
        title: `Deactivate ${yearObj.year} factors?`,
        message: `New calculations dated ${yearObj.year} will use the nearest active year's factors instead and be flagged as provisional.${usageMessage} Only deactivate a year to withdraw an incorrect factor set; leave older years active so historical data keeps using the correct factors.`,
        buttonCopy: 'Deactivate',
        cancelButtonCopy: 'Cancel',
      });
      if (!confirmed) return;
    }
    setConversionSource(previous => previous ? {
      ...previous,
      conversionYears: previous.conversionYears.map(existingYear => existingYear.year === yearObj.year ? { ...existingYear, active: !existingYear.active } : existingYear)
    } : previous);
    await conversionSourceYearService.update(conversionSourceId!, { year: yearObj.year, isActive: !yearObj.active })
      .then(() => {
        getConversionSource();
        userNotificationsContext?.openSnackbar({ open: true, message: 'Updated successfully' });
      })
      .catch(error => {
        console.error('Failed to update year status:', error);

        userNotificationsContext?.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      });
  };

  const handleDelete = (fileUploadId: string) => {
    if (!fileUploadId || fileUploadId === '') return;
    userNotificationsContext?.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this file? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response) {
          fileUploadService.deleteUpload(fileUploadId)
            .then(() => {
              getConversionSource();
              userNotificationsContext?.openSnackbar({ open: true, message: 'Deleted successfully' });
            })
            .catch((error) => {
              console.log(error)
              userNotificationsContext?.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            })
        }
      })
      .catch((error) => {
        console.log(error)
        userNotificationsContext?.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      })
  }

  const submitHandler = ({ file, year, gwpVersion, conversionSourceId, conversionType, sheetNumber, headerRow, }: { file: File; year: string; gwpVersion: string; conversionSourceId: string, conversionType: 'conversion' | 'squareFootConversion' | 'spendBasedConversion', sheetNumber: number; headerRow: number }) => {
    if (!conversionSourceId) return;
    const form = new FormData();
    form.append('files', file);
    form.append('details', JSON.stringify('conversion-factors'));
    form.append('sheetNumber', JSON.stringify(sheetNumber));
    form.append('year', JSON.stringify(year));
    form.append('gwpVersion', JSON.stringify(gwpVersion));
    form.append('isCustomSpreadSheet', JSON.stringify(true));
    form.append('conversionSourceId', JSON.stringify(conversionSourceId));
    form.append('conversionType', JSON.stringify(conversionType));
    form.append('headingRow', JSON.stringify(headerRow));
    fileUploadService.uploadFile(form)
      .then(response => {
        setIsUploadDialogOpen(false);
        navigate(`/super-admin/conversion-factors/${conversionSourceId}/${year}/mapping/${response.id}`);
      })
      .catch(() => setIsUploadDialogOpen(false));
  };

  const handleSelected = (ids: string[], mapped: boolean, fileUploadId: string) => {
    if (conversionSourceId === gaiaConversionSourceId && activeTab === 1) {
      if (ids[0]) navigate(`/super-admin/conversion-factors/${conversionSourceId}/${ids[0]}/edit-conversion-factor`);
    } else if (!mapped) {
      if (ids[0]) navigate(`/super-admin/conversion-factors/${conversionSourceId}/${ids[0]}/mapping/${fileUploadId}`);
    } else {
      if (ids[0]) navigate(`/super-admin/conversion-factors/${conversionSourceId}/${ids[0]}`);
    }
  };

  const handleAddConversionYear = (newYear: string) => {
    if (!conversionSourceId) return;
    conversionFactorCustomService.createConversionSourceYear(conversionSourceId, newYear)
      .then(() => {
        setIsConversionYearDialogOpen(false);
        getConversionSource();
        userNotificationsContext?.openSnackbar({ open: true, message: 'Saved successfully' });
      })
      .catch((error) => {
        console.log(error)
        userNotificationsContext?.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      });
  }

  return (
    <PageOuterContainer>
      {conversionSource &&
        <>
          <PageHeading
            title={`${conversionSource.source}`}
            includeBackButton={true}
            backButtonLink='/super-admin/conversion-factors'
            backButtonText='Back to conversion sources'
            includeActionButton={true}
            actionButtonIcon={conversionSourceId === gaiaConversionSourceId ? IconName.Add : IconName.UploadOutlined}
            actionButtonText={conversionSourceId === gaiaConversionSourceId ? 'Add New Conversion Year' : 'Import Conversion Factors'}
            actionButtonHandler={conversionSourceId === gaiaConversionSourceId ? () => setIsConversionYearDialogOpen(true) : () => setIsUploadDialogOpen(true)}
            includeSecondaryActionButton={conversionSourceId === gaiaConversionSourceId ? true : false}
            secondaryActionButtonText={conversionSourceId === gaiaConversionSourceId ? 'Add Conversion Factor' : ''}
            secondaryActionButtonIcon={conversionSourceId === gaiaConversionSourceId ? IconName.Add : undefined}
            secondaryActionButtonHandler={conversionSourceId === gaiaConversionSourceId ? () => navigate(`/super-admin/conversion-factors/${conversionSourceId}/add-conversion-factor`) : undefined}
          />
          {conversionSourceId === gaiaConversionSourceId &&
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
              <Tab label='Conversion Years' />
              <Tab label='Conversion Factors' />
            </Tabs>}
          {activeTab === 0 &&
            <TableDisplay
              headCells={headCells}
              rows={rows}
              selectByIcon={true}
              selected={selected}
              setSelected={setSelected}
              inputPage={0}
              setInputPage={() => { }}
              totalRecords={rows.length}
              perPage={rows.length}
              setPerPage={() => { }}
              onRowClick={(year) => {
                const yearObj = sortedYearsForTable.find(existingYear => existingYear.year === year);
                if (yearObj) handleSelected([yearObj.year], yearObj.mapped, yearObj.fileUploadId ?? '');
              }}
            />}
          {activeTab === 1 && (
            <>
              {conversionSourceId === gaiaConversionSourceId && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size='small'
                      label='Search detail levels'
                      value={secrSearchInput}
                      onChange={(e) => setSecrSearchInput(e.target.value)}
                      sx={{ minWidth: 220 }}
                    />
                    <TextField
                      select
                      size='small'
                      label='SECR status'
                      value={secrMappingFilter}
                      onChange={(e) => setSecrMappingFilter(e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value='legacy'>Legacy (not set)</MenuItem>
                      <MenuItem value='unmapped'>Unmapped</MenuItem>
                      <MenuItem value='suggested'>Suggested</MenuItem>
                      <MenuItem value='confirmed'>Confirmed</MenuItem>
                    </TextField>
                    <TextField
                      select
                      size='small'
                      label='Scope'
                      value={secrScopeFilter}
                      onChange={(e) => setSecrScopeFilter(e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value='1'>1</MenuItem>
                      <MenuItem value='2'>2</MenuItem>
                      <MenuItem value='3'>3</MenuItem>
                    </TextField>
                  </Box>
                </Box>
              )}
              {conversionFactorCustoms && conversionSource && (
                <TableDisplay
                  headCells={headCellsCustom}
                  rows={rowsCustom}
                  selectByIcon={true}
                  selected={selected}
                  setSelected={setSelected}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  onOrderChange={(newOrderBy, newOrder) => {
                    setOrderBy(newOrderBy);
                    setOrder(newOrder);
                  }}
                  onRowClick={(id) => handleSelected([id], true, '')}
                />
              )}
            </>
          )}
          {isConversionYearDialogOpen && conversionSourceId === gaiaConversionSourceId &&
            <ConversionFactorCustomAddNewYearDialog
              open={isConversionYearDialogOpen}
              onClose={() => setIsConversionYearDialogOpen(false)}
              availableYears={availableYears}
              onSubmit={handleAddConversionYear}
            />}
          {conversionSourceId !== gaiaConversionSourceId && isUploadDialogOpen && conversionSource &&
            <ConversionFactorsUploadDialog
              open={isUploadDialogOpen}
              onClose={() => setIsUploadDialogOpen(false)}
              availableYears={availableYears}
              onSubmit={submitHandler}
              conversionSourceId={conversionSource.id}
              conversionType={conversionSource.conversionType}
            />}
        </>}
    </PageOuterContainer >
  );
}

